import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FilePenLine,
  FileText,
  Info,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import config from "../../config";
import { getFormDetailsUrl } from "./formData";

export default function FormCard({ form }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const parseJsonResponse = async (response) => {
    const responseText = await response.text();

    if (!responseText.trim()) {
      throw new Error(
        `Server returned an empty response. HTTP ${response.status}.`
      );
    }

    try {
      return JSON.parse(responseText);
    } catch {
      console.error(
        "Invalid eligibility.php response:",
        responseText
      );

      throw new Error(
        "Server returned an invalid response. Check the PHP error log."
      );
    }
  };

  const fillForm = async () => {
    if (loading) {
      return;
    }

    const formName = String(form?.name || "").trim();
    const fillRoute = String(form?.route || "").trim();

    if (!formName) {
      toast.error("Form name is missing in the database.");
      return;
    }

    if (!fillRoute) {
      toast.error("Fill route is missing in the database.");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        `${config.API_URL}/eligibility.php`,
        {
          method: "POST",
          credentials: "include",
          cache: "no-store",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            form_name: formName,
          }),
        }
      );

      const result = await parseJsonResponse(response);

      if (response.status === 401) {
        toast.error(
          result.message ||
            "Your login has expired. Please login again."
        );

        navigate("/login", {
          replace: true,
        });

        return;
      }

      if (response.status === 403) {
        toast.error(
          result.message ||
            "You are not allowed to access this form."
        );

        return;
      }

      if (response.status === 402) {
        toast.error(
          result.message ||
            "Insufficient wallet balance."
        );

        return;
      }

      if (!response.ok || !result.success) {
        throw new Error(
          result.message ||
            `Eligibility check failed. HTTP ${response.status}.`
        );
      }

      toast.success(
        result.message || "Eligibility verified."
      );

      navigate(fillRoute);
    } catch (error) {
      console.error("Form eligibility error:", error);

      toast.error(
        error instanceof Error
          ? error.message
          : "Unable to connect to the server."
      );
    } finally {
      setLoading(false);
    }
  };

  /*
   * Check eligibility first, then open the modification list.
   * eligibility.php sets the FormStart session on success.
   */
  const modifyForm = async () => {
    if (loading) {
      return;
    }

    const formName = String(form?.name || "").trim();
    const modifyRoute = String(
      form?.modifyroute || ""
    ).trim();

    if (!formName) {
      toast.error("Form name is missing in the database.");
      return;
    }

    if (!modifyRoute) {
      toast.info(
        `Modify option is not currently available for ${form.title}.`
      );
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        `${config.API_URL}/eligibility.php`,
        {
          method: "POST",
          credentials: "include",
          cache: "no-store",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            form_name: formName,
          }),
        }
      );

      const result = await parseJsonResponse(response);

      if (response.status === 401) {
        toast.error(
          result.message ||
            "Your login has expired. Please login again."
        );

        navigate("/login", {
          replace: true,
        });

        return;
      }

      if (response.status === 403) {
        toast.error(
          result.message ||
            "You are not allowed to access this form."
        );
        return;
      }

      if (response.status === 402) {
        toast.error(
          result.message ||
            "Insufficient wallet balance."
        );
        return;
      }

      if (!response.ok || !result.success) {
        throw new Error(
          result.message ||
            `Eligibility check failed. HTTP ${response.status}.`
        );
      }

      /*
       * eligibility.php has successfully verified the form
       * and set the FormStart session.
       */
      toast.success(
        result.message || "Eligibility verified."
      );

      navigate(modifyRoute, {
        state: {
          selectedForm: {
            id: form.id,
            name: form.name,
            title: form.title,
            charge: form.charge,
            route: form.route,
            modifyroute: modifyRoute,
            sampleroute: form.sampleroute,
            module: form.module,
          },
        },
      });
    } catch (error) {
      console.error("Modify eligibility error:", error);

      toast.error(
        error instanceof Error
          ? error.message
          : "Unable to connect to the server."
      );
    } finally {
      setLoading(false);
    }
  };

  const showDetails = () => {
    if (loading) {
      return;
    }

    navigate(getFormDetailsUrl(form));
  };

  const modifyAvailable = Boolean(
    String(form?.modifyroute || "").trim()
  );

  return (
    <article className="group overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
      <div className="relative flex h-28 items-center justify-center bg-muted/20">
        <img
          src={form.image}
          alt={form.title}
          className="h-16 max-w-[75%] object-contain transition-transform duration-300 group-hover:scale-105"
        />

        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/70 backdrop-blur-[2px]">
            <div className="glass-card flex items-center gap-2 rounded-full px-3 py-2 text-xs font-semibold text-primary">
              <Loader2 className="h-4 w-4 animate-spin" />
              Checking eligibility...
            </div>
          </div>
        )}
      </div>

      <div className="p-3.5">
        <h3 className="min-h-[40px] text-center text-sm font-semibold leading-5">
          {form.title}
        </h3>

        <div className="mt-3 grid grid-cols-2 gap-2">
          {/* Fill button */}
          <button
            type="button"
            onClick={fillForm}
            disabled={loading}
            className="btn-gradient ripple col-span-2 flex h-10 items-center justify-center gap-2 rounded-full text-sm font-semibold text-white disabled:cursor-wait disabled:opacity-65"
            aria-label={`Fill ${form.title}`}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileText className="h-4 w-4" />
            )}

            {loading ? "Checking..." : "Fill"}
          </button>

          {/* Modify button */}
          <button
            type="button"
            onClick={modifyForm}
            disabled={loading}
            className={`flex h-9 items-center justify-center gap-1.5 rounded-full border text-xs font-semibold transition-colors disabled:opacity-50 ${
              modifyAvailable
                ? "border-border bg-background/50 text-muted-foreground hover:border-primary/30 hover:bg-primary/5 hover:text-primary"
                : "border-border/50 bg-muted/30 text-muted-foreground/60"
            }`}
            aria-label={`Modify ${form.title}`}
            title={
              modifyAvailable
                ? `Modify ${form.title}`
                : "Modify option is not yet available"
            }
          >
            <FilePenLine className="h-3.5 w-3.5" />
            Modify
          </button>

          {/* Details button */}
          <button
            type="button"
            onClick={showDetails}
            disabled={loading}
            className="flex h-9 items-center justify-center gap-1 rounded-full border border-border bg-background/50 text-xs font-semibold text-muted-foreground transition-colors hover:border-primary/30 hover:bg-primary/5 hover:text-primary disabled:opacity-50"
            aria-label={`View details for ${form.title}`}
          >
            <Info className="h-3.5 w-3.5" />
            Details
          </button>
        </div>
      </div>
    </article>
  );
}
